<template>
  <div
    class="page-content"
    :style="{
      paddingTop: infoHeaderBanner?.height && infoHeaderBanner?.message ? `${infoHeaderBanner?.height}px` : `0px`
    }"
  >
    <template v-if="isBlockPage">
      <div class="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-[1000] h-full">
        <div class="spinner"></div>
      </div>
    </template>

    <NuxtLoadingIndicator
      color="#09f"
      :throttle="200"
      :duration="100"
    />

    <VercelCodes v-if="confGlobal.ISRPreset" />
    
    <div class="min-h-[100vh] flex flex-col justify-between">
      <Header v-if="!hideWidget(confWidgets.header) || (props.type === 'error' || error)"></Header>

      <main class="flex-1">
        <template v-if="props.type === 'error' || error">
          <LazyHelpersLoadingError 
            v-if="error && error.statusCode !== 404"
            :data="error"
          />

          <LazyPagesNotFound v-else />
        </template>

        <template v-else>
          <NuxtPage />
        </template>
      </main>

      <LazyFooter v-if="!hideWidget(confWidgets.footer) || (props.type === 'error' || error)"></LazyFooter>
    </div>

    <DelayHydration>
      <ClientOnly>
        <LazyPagesPreviewMode v-if="$member?.isAdminPreview?.value && $member?.user?.value" />
            
        <template v-if="!$member?.isMember?.value">
          <LazyPopupsSubscribe
            v-if="confWidgets.PopupsSubscribe.widgetEnable && !hideWidget(confWidgets.PopupsSubscribe)"
          />
        </template>

        <LazyPopupsCookiesAgree
          v-if="confWidgets.popups.cookiesAgree.widgetEnable && activateRender"
        />

        <LazyPopupsUnsupportedBrowser
          v-if="confWidgets.PopupsUnsupportedBrowser.widgetEnable && !useNuxtApp().$isSupportedBrowser"
        />

        <LazyPopupsToastExpired />

        <template
          v-for="option, i in drawerOptions"
          :key="i"
        >
          <LazyPopupsCustomDrawer
            v-if="option && Object.keys(option).length"
            :id="i"
            @close="drawerOptions[i] = false"
            @submit="option.submit($event)"
          />
        </template>

        <ConfirmDialog
          ref="confirmPopupRef"
          :dismissable-mask="true"
          :draggable="false"
          :pt="{
            isConfirmDialog: true
          }"
        >
          <template #message="{ message }">
            <div 
              v-html="message.message"
            >
            </div>
          </template>
        </ConfirmDialog>
      </ClientOnly>
    </DelayHydration>

    <Toast
      position="bottom-right"
    />

    <Toast
      position="center"
      group="important"
    />

    <Toast
      position="bottom-right"
      group="html"
    >
      <template #message="slotProps">
        <div v-html="slotProps.message?.detail"></div>
      </template>
    </Toast>
  </div>
</template>

<script setup>
const error = useError()

const { $member } = useNuxtApp()
const { confGlobal, confWidgets, confScheme } = useConfig()
const { isProcessBlockPage, infoHeaderBanner } = useHelpers()
const { hideWidget } = useWidgets()
const { drawerOptions, confirmPopupRef } = usePopups()

const activateRender = ref(false)

/**
* Props
*/
const props = defineProps({
  type: {
    type: [String],
    default: null
  }
})

/**
* is Block Page
*/
const isBlockPage = computed(() => isProcessBlockPage.value && Object.values(isProcessBlockPage.value).some(value => value === true))

/**
* Meta Tags
*/
useHead({
  link: [
    {
      rel: 'apple-touch-icon',
      sizes: '180x180',
      href: '/img/apple-touch-icon.png'
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: '/img/favicon-32x32.png'
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: '/img/favicon-16x16.png'
    },
    { 
      rel: 'icon', 
      type: 'image/svg+xml', 
      href: '/img/favicon.svg' 
    },
    { 
      rel: 'icon', 
      type: 'image/x-icon', 
      href: '/img/favicon.ico' 
    }
  ]
})

/*
* Custom Event Queue
* Example: 
* useEventQueue().add('toast', { severity: 'error', detail: response?._data?.data.toString() ? response?._data?.data.toString() : response?._data?.message, life: 5000 })
*/
const toast = useToast()

watch(useEventQueue().eventQueue, () => {
  while (useEventQueue().eventQueue.value.length) {
    const event = useEventQueue().eventQueue.value.shift()

    if (event.type === 'toast') {
      toast.add(event.event)
    }
  }
}, { deep: true })

/**
* Delay Render
*/
const setTrueAfterFirstInteraction = () => {
  if (!activateRender.value) {
    activateRender.value = true

    window.removeEventListener('click', setTrueAfterFirstInteraction)
    window.removeEventListener('keydown', setTrueAfterFirstInteraction)
    window.removeEventListener('scroll', setTrueAfterFirstInteraction)
    window.removeEventListener('mousemove', setTrueAfterFirstInteraction)
  }
}

onNuxtReady(async () => {
  window.addEventListener('click', setTrueAfterFirstInteraction)
  window.addEventListener('keydown', setTrueAfterFirstInteraction)
  window.addEventListener('scroll', setTrueAfterFirstInteraction)
  window.addEventListener('mousemove', setTrueAfterFirstInteraction)

  setTimeout(() => {
    setTrueAfterFirstInteraction()
  }, 4000)
})

/**
* Json Schema
*/
useHead({
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(confScheme.main)
    }
  ]
})
</script>

<style lang="scss">
</style>
